/* Prest store locator — reads a published Google Sheet CSV, renders a
   searchable store list, and geocodes addresses automatically (Nominatim /
   OpenStreetMap, throttled and cached in localStorage) so the sheet only
   needs Store + Address columns.

   Search behavior:
   - State (name or 2-letter code): every store in that state
   - Zip code: stores in that zip, plus optional "within X miles" radius
   - City (anything else): stores matching the text, plus optional radius */

(function () {
  'use strict';

  var GEO_CACHE_KEY = 'prestGeoCacheV1';

  var STATES = {
    al: 'alabama', ak: 'alaska', az: 'arizona', ar: 'arkansas', ca: 'california',
    co: 'colorado', ct: 'connecticut', de: 'delaware', fl: 'florida', ga: 'georgia',
    hi: 'hawaii', id: 'idaho', il: 'illinois', in: 'indiana', ia: 'iowa',
    ks: 'kansas', ky: 'kentucky', la: 'louisiana', me: 'maine', md: 'maryland',
    ma: 'massachusetts', mi: 'michigan', mn: 'minnesota', ms: 'mississippi', mo: 'missouri',
    mt: 'montana', ne: 'nebraska', nv: 'nevada', nh: 'new hampshire', nj: 'new jersey',
    nm: 'new mexico', ny: 'new york', nc: 'north carolina', nd: 'north dakota', oh: 'ohio',
    ok: 'oklahoma', or: 'oregon', pa: 'pennsylvania', ri: 'rhode island', sc: 'south carolina',
    sd: 'south dakota', tn: 'tennessee', tx: 'texas', ut: 'utah', vt: 'vermont',
    va: 'virginia', wa: 'washington', wv: 'west virginia', wi: 'wisconsin', wy: 'wyoming',
    dc: 'district of columbia'
  };

  function parseCSV(text) {
    var rows = [], row = [], field = '', inQuotes = false;
    for (var i = 0; i < text.length; i++) {
      var c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; }
          else inQuotes = false;
        } else field += c;
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === ',') {
        row.push(field); field = '';
      } else if (c === '\n' || c === '\r') {
        if (field !== '' || row.length) { row.push(field); rows.push(row); row = []; field = ''; }
        if (c === '\r' && text[i + 1] === '\n') i++;
      } else field += c;
    }
    if (field !== '' || row.length) { row.push(field); rows.push(row); }
    return rows;
  }

  function normalizeHeader(h) {
    return h.trim().toLowerCase().replace(/[^a-z]/g, '');
  }

  function toStores(rows) {
    if (!rows.length) return [];
    var headers = rows[0].map(normalizeHeader);
    var idx = function (names) {
      return headers.findIndex(function (h) { return names.indexOf(h) !== -1; });
    };
    var iName = idx(['store', 'name', 'storename']);
    var iAddr = idx(['address', 'street', 'streetaddress']);
    var iCity = idx(['city', 'town']);
    var iState = idx(['state', 'province']);
    var iZip = idx(['zip', 'zipcode', 'postalcode']);
    var iNote = idx(['note', 'notes', 'hours']);
    var iLat = idx(['lat', 'latitude']);
    var iLng = idx(['lng', 'long', 'longitude']);
    return rows.slice(1).map(function (r) {
      return {
        name: iName >= 0 ? (r[iName] || '').trim() : '',
        address: iAddr >= 0 ? (r[iAddr] || '').trim() : '',
        city: iCity >= 0 ? (r[iCity] || '').trim() : '',
        state: iState >= 0 ? (r[iState] || '').trim() : '',
        zip: iZip >= 0 ? (r[iZip] || '').trim() : '',
        note: iNote >= 0 ? (r[iNote] || '').trim() : '',
        lat: iLat >= 0 ? parseFloat(r[iLat]) : NaN,
        lng: iLng >= 0 ? parseFloat(r[iLng]) : NaN,
      };
    }).filter(function (s) { return s.name; });
  }

  function addrKey(s) {
    return [s.address, s.city, s.state, s.zip].filter(Boolean).join(', ').toLowerCase();
  }

  function loadGeoCache() {
    try { return JSON.parse(localStorage.getItem(GEO_CACHE_KEY)) || {}; }
    catch (e) { return {}; }
  }

  function saveGeoCache(cache) {
    try { localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(cache)); }
    catch (e) { /* storage blocked — lookups repeat next visit */ }
  }

  function hasCoords(s) {
    return !isNaN(s.lat) && !isNaN(s.lng);
  }

  function milesBetween(lat1, lng1, lat2, lng2) {
    var toRad = function (d) { return (d * Math.PI) / 180; };
    var R = 3958.8;
    var dLat = toRad(lat2 - lat1);
    var dLng = toRad(lng2 - lng1);
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /* state | zip | city */
  function classifyQuery(q) {
    var t = q.trim().toLowerCase();
    if (!t) return { mode: 'none' };
    if (/^\d{5}$/.test(t)) return { mode: 'zip', zip: t };
    if (STATES[t]) return { mode: 'state', abbrev: t, full: STATES[t] };
    for (var ab in STATES) {
      if (STATES[ab] === t) return { mode: 'state', abbrev: ab, full: t };
    }
    return { mode: 'city', text: t };
  }

  function storeCard(s, distanceMi) {
    var q = encodeURIComponent([s.name, s.address, s.city, s.state, s.zip].filter(Boolean).join(', '));
    var card = document.createElement('div');
    card.className = 'store-card';
    card.innerHTML =
      '<p class="store-card__name"></p>' +
      '<p class="store-card__addr"></p>' +
      (s.note ? '<p class="store-card__note"></p>' : '') +
      (typeof distanceMi === 'number' ? '<p class="store-card__distance"></p>' : '') +
      '<a class="store-card__directions link" target="_blank" rel="noopener" href="https://www.google.com/maps/search/?api=1&query=' + q + '">Get directions →</a>';
    card.querySelector('.store-card__name').textContent = s.name;
    card.querySelector('.store-card__addr').textContent = [s.address, s.city, s.state, s.zip].filter(Boolean).join(', ');
    if (s.note) card.querySelector('.store-card__note').textContent = s.note;
    if (typeof distanceMi === 'number') {
      card.querySelector('.store-card__distance').textContent = distanceMi < 1 ? 'Less than a mile away' : '~' + Math.round(distanceMi) + ' miles away';
    }
    return card;
  }

  function initLocator(root) {
    var csvUrl = root.dataset.csvUrl;
    var listEl = root.querySelector('[data-store-list]');
    var searchEl = root.querySelector('[data-store-search]');
    var radiusEl = root.querySelector('[data-store-radius]');
    var statusEl = root.querySelector('[data-store-status]');
    var countEl = root.querySelector('[data-store-count]');
    var mapEl = root.querySelector('[data-store-map]');
    var stores = [];
    var map = null;
    var markers = [];
    var leafletLoading = false;
    var lastQuery = '';
    var searchCenter = null; /* { key, lat, lng } */
    var centerLookupPending = null;
    var debounceTimer = null;

    function textMatch(s, t) {
      return [s.name, s.address, s.city, s.state, s.zip].join(' ').toLowerCase().indexOf(t) !== -1;
    }

    function radiusMiles() {
      return radiusEl ? parseInt(radiusEl.value, 10) || 0 : 0;
    }

    /* returns [{store, distance?}] */
    function currentVisible() {
      var info = classifyQuery(lastQuery);
      var radius = radiusMiles();

      if (info.mode === 'none') {
        return stores.map(function (s) { return { store: s }; });
      }

      if (info.mode === 'state') {
        return stores.filter(function (s) {
          var st = (s.state || '').trim().toLowerCase();
          return st === info.abbrev || st === info.full;
        }).map(function (s) { return { store: s }; });
      }

      var center = searchCenter && searchCenter.key === centerKey() ? searchCenter : null;
      var results = [];
      stores.forEach(function (s) {
        var direct = info.mode === 'zip'
          ? (s.zip || '').indexOf(info.zip) === 0
          : textMatch(s, info.text);
        var dist = null;
        if (center && hasCoords(s)) {
          dist = milesBetween(center.lat, center.lng, s.lat, s.lng);
        }
        var inRadius = radius > 0 && dist !== null && dist <= radius;
        if (direct || inRadius) {
          results.push({ store: s, distance: dist !== null && radius > 0 ? dist : undefined });
        }
      });
      if (radius > 0) {
        results.sort(function (a, b) {
          return (a.distance === undefined ? 1e9 : a.distance) - (b.distance === undefined ? 1e9 : b.distance);
        });
      }
      return results;
    }

    function centerKey() {
      var info = classifyQuery(lastQuery);
      if (info.mode === 'zip') return 'zip:' + info.zip;
      if (info.mode === 'city') return 'city:' + info.text;
      return '';
    }

    function ensureSearchCenter() {
      var key = centerKey();
      if (!key || radiusMiles() === 0) return;
      if (searchCenter && searchCenter.key === key) return;
      if (centerLookupPending === key) return;
      var cache = loadGeoCache();
      var cacheKey = 'q:' + key;
      var hit = cache[cacheKey];
      if (hit && hit.length === 2) {
        searchCenter = { key: key, lat: hit[0], lng: hit[1] };
        render();
        return;
      }
      if (hit === null) return; /* known miss */
      centerLookupPending = key;
      var info = classifyQuery(lastQuery);
      var q = info.mode === 'zip' ? info.zip + ', USA' : info.text + ', USA';
      fetch('https://nominatim.openstreetmap.org/search?format=json&limit=1&q=' + encodeURIComponent(q))
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data && data[0]) {
            cache[cacheKey] = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
            searchCenter = { key: key, lat: cache[cacheKey][0], lng: cache[cacheKey][1] };
          } else {
            cache[cacheKey] = null;
          }
          saveGeoCache(cache);
        })
        .catch(function () { /* offline — text results still show */ })
        .then(function () {
          centerLookupPending = null;
          render();
        });
    }

    function render() {
      var info = classifyQuery(lastQuery);
      /* radius dropdown only applies to city/zip searches */
      if (radiusEl) {
        radiusEl.hidden = !(info.mode === 'city' || info.mode === 'zip');
      }

      var visible = currentVisible();
      listEl.textContent = '';
      visible.forEach(function (v) { listEl.appendChild(storeCard(v.store, v.distance)); });

      if (countEl) {
        if (!visible.length) {
          countEl.textContent = centerLookupPending
            ? 'Searching nearby…'
            : 'No locations match — try a different city, state, or zip';
        } else {
          var suffix = '';
          if (radiusMiles() > 0 && (info.mode === 'city' || info.mode === 'zip')) {
            suffix = ' within ' + radiusMiles() + ' miles';
          }
          countEl.textContent = visible.length + ' location' + (visible.length === 1 ? '' : 's') + suffix;
        }
      }
      updateMap(visible.map(function (v) { return v.store; }));
    }

    function updateMap(visibleStores) {
      if (!map) return;
      markers.forEach(function (m) { m.remove(); });
      markers = [];
      var withCoords = visibleStores.filter(hasCoords);
      withCoords.forEach(function (s) {
        var m = window.L.marker([s.lat, s.lng]).addTo(map);
        m.bindPopup('<strong>' + s.name.replace(/</g, '&lt;') + '</strong><br>' + [s.address, s.city].filter(Boolean).join(', ').replace(/</g, '&lt;'));
        markers.push(m);
      });
      if (withCoords.length) {
        var bounds = window.L.latLngBounds(withCoords.map(function (s) { return [s.lat, s.lng]; }));
        map.fitBounds(bounds.pad(0.2), { maxZoom: 12 });
      }
    }

    function ensureMap() {
      if (map || leafletLoading || !mapEl) return;
      if (!stores.some(hasCoords)) return;
      leafletLoading = true;
      var css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(css);
      var js = document.createElement('script');
      js.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      js.onload = function () {
        mapEl.hidden = false;
        map = window.L.map(mapEl, { scrollWheelZoom: false });
        window.L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(map);
        render();
      };
      js.onerror = function () { mapEl.hidden = true; };
      document.head.appendChild(js);
    }

    function geocodeMissing() {
      var cache = loadGeoCache();

      stores.forEach(function (s) {
        if (hasCoords(s)) return;
        var hit = cache[addrKey(s)];
        if (hit && hit.length === 2) { s.lat = hit[0]; s.lng = hit[1]; }
      });
      ensureMap();

      var queue = stores.filter(function (s) {
        return !hasCoords(s) && addrKey(s) && cache[addrKey(s)] === undefined;
      });

      function next() {
        if (!queue.length) return;
        var s = queue.shift();
        var key = addrKey(s);
        fetch('https://nominatim.openstreetmap.org/search?format=json&limit=1&q=' + encodeURIComponent(key))
          .then(function (res) { return res.json(); })
          .then(function (data) {
            if (data && data[0]) {
              s.lat = parseFloat(data[0].lat);
              s.lng = parseFloat(data[0].lon);
              cache[key] = [s.lat, s.lng];
            } else {
              cache[key] = null;
            }
            saveGeoCache(cache);
            ensureMap();
            render();
          })
          .catch(function () { /* offline — retry next visit */ })
          .then(function () { setTimeout(next, 1100); });
      }
      next();
    }

    if (!csvUrl) {
      statusEl.textContent = 'Store list coming soon!';
      return;
    }

    fetch(csvUrl)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (text) {
        stores = toStores(parseCSV(text));
        statusEl.hidden = true;
        if (!stores.length) {
          statusEl.hidden = false;
          statusEl.textContent = 'Store list coming soon!';
          return;
        }
        root.querySelector('[data-store-controls]').hidden = false;
        render();
        geocodeMissing();
      })
      .catch(function () {
        statusEl.textContent = 'We couldn’t load the store list right now — please try again in a minute.';
      });

    if (searchEl) {
      searchEl.addEventListener('input', function () {
        lastQuery = searchEl.value;
        render();
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(ensureSearchCenter, 600);
      });
    }
    if (radiusEl) {
      radiusEl.addEventListener('change', function () {
        render();
        ensureSearchCenter();
      });
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-store-locator]').forEach(initLocator);
  });
})();
